import React from 'react'
import NextLink from 'next/link'
import { Link as MuiLink } from '@mui/material'
import { Text } from '@fundamentals'
import { useGetOrganisation } from '@core/react-query/features/organisations/organisation/hooks/useGetOrganisation'

type TemplateSummary = {
  id?: number
  name: string
  currentVersion?: { id?: number; version: number }
}

type ComponentType = {
  organisationId: number
  template: TemplateSummary
  /** e.g. PageUrls.editTemplate / PageUrls.editChecklistTemplate */
  getTemplateUrl: (orgId: number, templateId: number) => string
  /** e.g. PageUrls.editTemplateVersion / PageUrls.editChecklistTemplateVersion */
  getVersionUrl: (args: {
    orgId: number
    templateId: number
    versionId: number
  }) => string
  reference?: string
  dataTest?: string
}

type TitlePartProps = {
  url?: string
  dataTest: string
  children: React.ReactNode
}

const TitlePart: React.FC<TitlePartProps> = ({ url, dataTest, children }) => {
  if (!url) {
    return <>{children}</>
  }

  return (
    <MuiLink
      component={NextLink}
      href={url}
      prefetch={false}
      color='inherit'
      underline='hover'
      data-test={dataTest}
    >
      {children}
    </MuiLink>
  )
}

const TemplateVersionTitle: React.FC<ComponentType> = ({
  organisationId,
  template,
  getTemplateUrl,
  getVersionUrl,
  reference,
  dataTest,
}) => {
  // Both targets live under organisation settings, so gate on the same check as
  // the "Organisation Settings" menu item.
  const { data: organisation } = useGetOrganisation({ organisationId })
  const canLink = (organisation?.canEdit() ?? false) && !!template.id

  const version = template.currentVersion?.version
  const versionId = template.currentVersion?.id

  return (
    <Text variant='h5' data-test={dataTest}>
      <TitlePart
        url={canLink ? getTemplateUrl(organisationId, template.id) : undefined}
        dataTest='template-name-link'
      >
        {template.name}
      </TitlePart>
      {version != null && (
        <>
          {' ('}
          <TitlePart
            url={
              canLink && versionId
                ? getVersionUrl({
                    orgId: organisationId,
                    templateId: template.id,
                    versionId,
                  })
                : undefined
            }
            dataTest='template-version-link'
          >
            {`V${version}`}
          </TitlePart>
          {')'}
        </>
      )}
      {reference ? ` - ${reference}` : ''}
    </Text>
  )
}

export { TemplateVersionTitle }
